/**
 * ADR-0013 — Sale payment allocation (full / credit / mixed cash|electronic + créance).
 */

import { resolveSaleUnpaidAmount } from '../../customers/balance/saleBalance';
import { getSaleTotalAmount } from '../../restaurant/restaurantDishSaleService';

export { resolveSaleUnpaidAmount };

/**
 * Bezahlt jetzt (Kassen-Einnahme). Créance-Anteil = Total − unpaid.
 * @param {object} [sale]
 * @returns {number}
 */
export function resolveSalePaidAmount(sale) {
  if (!sale) return 0;
  const total = getSaleTotalAmount(sale);
  if (total <= 0) return 0;
  const unpaid = resolveSaleUnpaidAmount(sale);
  return Math.max(0, Math.round((total - unpaid) * 100) / 100);
}

/**
 * Kanal der Kassen-Einnahme (ignoriert unpaid für Bucket).
 * @returns {'cash'|'electronic'|'credit'}
 */
export function resolveSalePaidChannel(sale = {}) {
  const paid = resolveSalePaidAmount(sale);
  if (paid <= 0) return 'credit';

  const paymentMethod = String(sale.paymentMethod || sale.paymentType || '').trim().toLowerCase();
  const provider = String(
    sale.electronicPaymentMethod || sale.payment?.paymentMethod || ''
  )
    .trim()
    .toLowerCase();

  if (
    paymentMethod === 'credit' ||
    paymentMethod === 'credit_payment' ||
    paymentMethod === 'on_account'
  ) {
    return 'credit';
  }
  if (paymentMethod === 'cash' || paymentMethod === 'espèces' || paymentMethod === 'especes') {
    return 'cash';
  }
  if (provider && provider !== 'cash' && provider !== 'espèces') return 'electronic';
  if (
    !paymentMethod ||
    paymentMethod === 'electronic' ||
    paymentMethod === 'électronique' ||
    paymentMethod === 'card' ||
    paymentMethod === 'mobile'
  ) {
    return 'electronic';
  }
  return paymentMethod === 'cash' ? 'cash' : 'electronic';
}

/**
 * @returns {{ amount: number, channel: 'cash'|'electronic'|'credit' }}
 */
export function resolveSaleRegisterIncome(sale) {
  const amount = resolveSalePaidAmount(sale);
  if (amount <= 0) return { amount: 0, channel: 'credit' };
  return { amount, channel: resolveSalePaidChannel(sale) };
}

/**
 * POS: volle Créance.
 */
export function attachCreditSaleFields(saleData = {}) {
  const total = Number(saleData.totalAmount ?? saleData.total ?? 0) || 0;
  return {
    ...saleData,
    paymentMethod: 'credit',
    unpaidAmount: total,
    creditAmount: total,
    paidAmount: 0,
    cashAmount: null,
    change: null,
    electronicPaymentMethod: null,
    isPartialCredit: false
  };
}

/**
 * POS: Acompte (cash|electronic) + Rest als Créance.
 * @param {object} saleData
 * @param {{
 *   paidAmountMro?: number,
 *   paidMethod?: 'cash'|'electronic',
 *   electronicPaymentMethod?: string|null,
 *   changeMro?: number|null
 * }} [options]
 */
export function attachMixedPaymentFields(saleData = {}, options = {}) {
  const total = Number(saleData.totalAmount ?? saleData.total ?? 0) || 0;
  const paidMethod = options.paidMethod === 'electronic' ? 'electronic' : 'cash';
  const paidRaw = Number(options.paidAmountMro);
  const paid = Number.isFinite(paidRaw)
    ? Math.min(Math.max(0, Math.round(paidRaw * 100) / 100), total)
    : 0;
  const unpaid = Math.max(0, Math.round((total - paid) * 100) / 100);

  if (unpaid <= 0) {
    return {
      ...saleData,
      paymentMethod: paidMethod,
      unpaidAmount: 0,
      creditAmount: 0,
      paidAmount: total,
      cashAmount: paidMethod === 'cash' ? total : null,
      change: paidMethod === 'cash' ? Number(options.changeMro) || 0 : null,
      electronicPaymentMethod:
        paidMethod === 'electronic' ? options.electronicPaymentMethod || null : null,
      isPartialCredit: false
    };
  }

  if (paid <= 0) {
    return attachCreditSaleFields(saleData);
  }

  return {
    ...saleData,
    paymentMethod: paidMethod,
    unpaidAmount: unpaid,
    creditAmount: unpaid,
    paidAmount: paid,
    cashAmount: paidMethod === 'cash' ? paid : null,
    change: 0,
    electronicPaymentMethod:
      paidMethod === 'electronic' ? options.electronicPaymentMethod || null : null,
    isPartialCredit: true
  };
}
